import { useLayoutEffect, type RefObject } from 'react';
import { placeTitlePlacement } from '../render/place-title-placement';

export function usePlaceTitlePlacement(root: RefObject<HTMLDivElement | null>, lockup: RefObject<HTMLDivElement | null>,
  overlay: RefObject<HTMLDivElement | null> | undefined, name: string, region: string | undefined) {
  useLayoutEffect(() => {
    const element = root.current;
    const text = lockup.current;
    const parent = element?.parentElement;
    if (!element || !text || !parent) return;
    const plates = () => [...(overlay?.current?.querySelectorAll<HTMLElement>('[data-composition-element]') ?? [])];
    const fit = () => {
      const bounds = parent.getBoundingClientRect();
      const measured = plates().map(plate => {
        const rect = plate.getBoundingClientRect();
        return { x: rect.left - bounds.left, y: rect.top - bounds.top, width: rect.width, height: rect.height,
          title: plate.dataset.compositionElement === 'Title plate' };
      });
      const nominalSize = Math.min(bounds.width * .145, bounds.height * .2);
      text.style.fontSize = `${nominalSize}px`;
      const natural = text.getBoundingClientRect();
      const topTitleBottom = Math.max(0, ...measured.filter(plate => plate.title && plate.y < bounds.height / 2)
        .map(plate => plate.y + plate.height));
      const placement = placeTitlePlacement(bounds, natural, measured, topTitleBottom);
      element.style.visibility = placement ? 'visible' : 'hidden';
      if (!placement) return;
      text.style.fontSize = `${nominalSize * placement.scale}px`;
      Object.assign(element.style, { left: `${placement.x}px`, top: `${placement.y}px`,
        width: `${placement.width}px`, height: `${placement.height}px` });
    };
    const resize = new ResizeObserver(fit);
    resize.observe(parent);
    const observePlates = () => { for (const plate of plates()) resize.observe(plate); };
    observePlates();
    // Layout selection and inset edits can move a plate without changing its size.
    const mutation = new MutationObserver(() => { observePlates(); fit(); });
    if (overlay?.current) mutation.observe(overlay.current, { attributes: true, attributeFilter: ['style', 'class', 'data-social-layout'],
      childList: true, subtree: true });
    fit();
    let active = true;
    void document.fonts.ready.then(() => { if (active) fit(); });
    return () => { active = false; resize.disconnect(); mutation.disconnect(); };
  }, [root, lockup, overlay, name, region]);
}
